import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function useTireEvents(tireId: string | undefined) {
  return useQuery({
    queryKey: ['tire-events', tireId],
    queryFn: async () => {
      if (!tireId) return [];
      
      const { data, error } = await supabase
        .from('fleet_tire_events')
        .select(`
          *,
          vehicle:fleet_vehicles(id, plate, make, model),
          work_order:fleet_work_orders(id, order_number, description)
        `)
        .eq('tire_id', tireId)
        .order('occurred_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!tireId
  });
}

export function useCreateTireEvent(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (event: any) => {
      if (!companyId) throw new Error('Company ID required');
      
      const { data, error } = await supabase
        .from('fleet_tire_events')
        .insert({
          ...event,
          company_id: companyId
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tire-events', data.tire_id] });
      queryClient.invalidateQueries({ queryKey: ['fleet-tires', companyId] });
      toast({
        title: "Evento registrado",
        description: "Evento do pneu registrado com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao registrar evento",
        description: error.message,
        variant: "destructive",
      });
    }
  });
}

export function useTireInstallation(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ tireId, vehicleId, position, vehicleKm }: any) => {
      if (!companyId) throw new Error('Company ID required');

      // Criar evento de instalação
      const { data: event, error: eventError } = await supabase
        .from('fleet_tire_events')
        .insert({
          company_id: companyId,
          tire_id: tireId,
          event_type: 'installation',
          occurred_at: new Date().toISOString(),
          vehicle_id: vehicleId,
          from_position: null,
          to_position: position,
          odometer_reading: vehicleKm
        })
        .select()
        .single();

      if (eventError) throw eventError;

      // Atualizar status do pneu
      const { error: tireError } = await supabase
        .from('fleet_tires')
        .update({
          status: 'installed',
          current_vehicle_id: vehicleId,
          current_position: position
        })
        .eq('id', tireId);

      if (tireError) throw tireError;

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-tires', companyId] });
      queryClient.invalidateQueries({ queryKey: ['tire-events'] });
      toast({
        title: "Pneu instalado",
        description: "Pneu instalado no veículo com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao instalar pneu",
        description: error.message,
        variant: "destructive",
      });
    }
  });
}

export function useTireRemoval(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ tireId, vehicleId, vehicleKm, reason }: any) => {
      if (!companyId) throw new Error('Company ID required');

      // Buscar posição atual
      const { data: tire } = await supabase
        .from('fleet_tires')
        .select('current_position')
        .eq('id', tireId)
        .single();

      // Criar evento de remoção
      const { data: event, error: eventError } = await supabase
        .from('fleet_tire_events')
        .insert({
          company_id: companyId,
          tire_id: tireId,
          event_type: 'removal',
          occurred_at: new Date().toISOString(),
          vehicle_id: vehicleId,
          from_position: tire?.current_position || null,
          to_position: null,
          odometer_reading: vehicleKm,
          reason: reason
        })
        .select()
        .single();

      if (eventError) throw eventError;

      // Atualizar status do pneu
      const { error: tireError } = await supabase
        .from('fleet_tires')
        .update({
          status: 'stock',
          current_vehicle_id: null,
          current_position: null
        })
        .eq('id', tireId);

      if (tireError) throw tireError;

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-tires', companyId] });
      queryClient.invalidateQueries({ queryKey: ['tire-events'] });
      toast({
        title: "Pneu removido",
        description: "Pneu removido do veículo com sucesso.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao remover pneu",
        description: error.message,
        variant: "destructive",
      });
    }
  });
}
